import { useRef, useState } from 'react';
import { cn } from '@ais-ui/utils';
import { Input } from './Input';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { PiClock } from 'react-icons/pi';
import { Button } from './Button';
import { ScrollArea } from './ScrollArea';

interface TimePickerProps {
  value?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
}

export default function TimePicker24Hrs({
  value,
  onChange,
  placeholder = 'HH:MM:SS',
  className,
}: TimePickerProps) {
  const [time, setTime] = useState(value || '00:00:00');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Parse time string to components, treating underscores as 0
  const parseTime = (timeStr: string) => {
    if (!timeStr || typeof timeStr !== 'string') {
      return { hours: 0, minutes: 0, seconds: 0 };
    }

    // Replace underscores with 0 for parsing
    const cleanTimeStr = timeStr.replace(/_/g, '0');
    const parts = cleanTimeStr.split(':');
    const hours = Math.max(0, Math.min(23, parseInt(parts[0]) || 0));
    const minutes = Math.max(0, Math.min(59, parseInt(parts[1]) || 0));
    const seconds = Math.max(0, Math.min(59, parseInt(parts[2]) || 0));

    return { hours, minutes, seconds };
  };

  // Format time components to string, preserving underscores in unchanged parts
  const formatTimePreservingUnderscores = (
    h: number,
    m: number,
    s: number,
    originalTime: string,
    editingSegment: 'hours' | 'minutes' | 'seconds'
  ) => {
    const hours = Math.max(0, Math.min(23, h));
    const minutes = Math.max(0, Math.min(59, m));
    const seconds = Math.max(0, Math.min(59, s));

    const hourStr = hours.toString().padStart(2, '0');
    const minuteStr = minutes.toString().padStart(2, '0');
    const secondStr = seconds.toString().padStart(2, '0');

    // Only preserve underscores in sections we're NOT currently editing
    const originalParts = originalTime.split(':');
    const finalHourStr =
      editingSegment === 'hours'
        ? hourStr
        : originalParts[0] && originalParts[0].includes('_')
        ? originalParts[0]
        : hourStr;
    const finalMinuteStr =
      editingSegment === 'minutes'
        ? minuteStr
        : originalParts[1] && originalParts[1].includes('_')
        ? originalParts[1]
        : minuteStr;
    const finalSecondStr =
      editingSegment === 'seconds'
        ? secondStr
        : originalParts[2] && originalParts[2].includes('_')
        ? originalParts[2]
        : secondStr;

    return `${finalHourStr}:${finalMinuteStr}:${finalSecondStr}`;
  };

  // Format time components to string
  const formatTime = (h: number, m: number, s: number) => {
    const hours = Math.max(0, Math.min(23, h));
    const minutes = Math.max(0, Math.min(59, m));
    const seconds = Math.max(0, Math.min(59, s));

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Convert underscores to zeros
  const convertUnderscoresToZeros = (timeStr: string) => {
    return timeStr.replace(/_/g, '0');
  };

  // Helper function to trigger onChange with proper event object
  const triggerChange = (newTime: string, forceUpdate = false) => {
    setTime(newTime);

    // Only trigger onChange if there are no underscores (complete time) or if forced
    if (
      (onChange && inputRef.current && !newTime.includes('_')) ||
      forceUpdate
    ) {
      // Create a synthetic event that matches React.ChangeEvent<HTMLInputElement>
      const syntheticEvent = {
        target: { ...inputRef.current, value: newTime },
        currentTarget: { ...inputRef.current, value: newTime },
        bubbles: true,
        cancelable: false,
        defaultPrevented: false,
        eventPhase: 0,
        isTrusted: false,
        nativeEvent: new Event('change'),
        preventDefault: () => {},
        isDefaultPrevented: () => false,
        stopPropagation: () => {},
        isPropagationStopped: () => false,
        persist: () => {},
        timeStamp: Date.now(),
        type: 'change',
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(syntheticEvent);
    }
  };

  // Handle direct input changes (typing in the input field)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setTime(newValue);
    // Only trigger parent onChange if no underscores present
    if (onChange && !newValue.includes('_')) {
      onChange(e);
    }
  };

  // Handle focus loss - convert underscores to zeros
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const cleanTime = convertUnderscoresToZeros(time);
    if (cleanTime !== time) {
      triggerChange(cleanTime, true); // Force update on blur to ensure parent gets clean value
    }
  };

  // Handle focus - position cursor at the seconds segment
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.currentTarget;

    setTimeout(() => {
      // Position cursor at the start of the seconds segment (position 6)
      input.setSelectionRange(6, 6);
    }, 0);
  };

  // Get cursor position in terms of time segments
  const getTimeSegment = (position: number) => {
    if (position <= 2) return 'hours';
    if (position <= 5) return 'minutes';
    return 'seconds';
  };

  // Handle key input - mimic native browser behavior more accurately
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorPos = input.selectionStart || 0;
    const currentSegment = getTimeSegment(cursorPos);

    // Allow basic navigation keys
    if (['Tab', 'Enter', 'Escape'].includes(e.key)) {
      return;
    }

    // Handle arrow keys - simple left/right navigation
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      // Let browser handle normal cursor movement, but skip over colons
      setTimeout(() => {
        const pos = input.selectionStart || 0;
        if (pos === 2 && e.key === 'ArrowRight') {
          input.setSelectionRange(3, 3);
        } else if (pos === 5 && e.key === 'ArrowRight') {
          input.setSelectionRange(6, 6);
        } else if (pos === 3 && e.key === 'ArrowLeft') {
          input.setSelectionRange(2, 2);
        } else if (pos === 6 && e.key === 'ArrowLeft') {
          input.setSelectionRange(5, 5);
        }
      }, 0);
      return;
    }

    // Handle up/down arrows to increment/decrement
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const { hours, minutes, seconds } = parseTime(time);
      const increment = e.key === 'ArrowUp' ? 1 : -1;
      let newHours = hours;
      let newMinutes = minutes;
      let newSeconds = seconds;

      if (currentSegment === 'hours') {
        newHours = Math.max(0, Math.min(23, hours + increment));
      } else if (currentSegment === 'minutes') {
        newMinutes = Math.max(0, Math.min(59, minutes + increment));
      } else if (currentSegment === 'seconds') {
        newSeconds = Math.max(0, Math.min(59, seconds + increment));
      }

      const newTime = formatTimePreservingUnderscores(
        newHours,
        newMinutes,
        newSeconds,
        time,
        currentSegment
      );
      triggerChange(newTime);

      // Maintain cursor position
      setTimeout(() => input.setSelectionRange(cursorPos, cursorPos), 0);
      return;
    }

    // Handle backspace - replace with underscore instead of 0
    if (e.key === 'Backspace') {
      e.preventDefault();

      // Don't do anything if at the very beginning
      if (cursorPos === 0) {
        return;
      }

      // Move cursor one position back
      let newCursorPos = cursorPos - 1;

      // If we're not deleting a colon, replace the character with underscore
      if (cursorPos !== 3 && cursorPos !== 6) {
        const timeArray = time.split('');
        timeArray[cursorPos - 1] = '_';
        const newTime = timeArray.join('');
        triggerChange(newTime);
      }

      // Set the cursor position
      setTimeout(() => input.setSelectionRange(newCursorPos, newCursorPos), 0);
      return;
    }

    // Handle numeric input
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const digit = parseInt(e.key);
      const { hours, minutes, seconds } = parseTime(time);

      if (currentSegment === 'hours') {
        const hourStr = hours.toString().padStart(2, '0');
        let newHourStr = '';
        let newCursorPos = cursorPos + 1;

        if (cursorPos === 0) {
          newHourStr = digit + hourStr[1];
          // Auto-advance if digit > 2 (can't be valid hour)
          if (digit > 2) {
            newCursorPos = 3;
          }
        } else if (cursorPos === 1) {
          newHourStr = hourStr[0] + digit;
          // Auto-advance after completing hours
          newCursorPos = 3;
        } else if (cursorPos === 2) {
          // At colon, move to minutes
          newHourStr = hourStr;
          newCursorPos = 3;
        }

        const newHours = Math.min(23, parseInt(newHourStr) || 0);
        const newTime = formatTimePreservingUnderscores(
          newHours,
          minutes,
          seconds,
          time,
          'hours'
        );
        triggerChange(newTime);
        setTimeout(
          () => input.setSelectionRange(newCursorPos, newCursorPos),
          0
        );
      } else if (currentSegment === 'minutes') {
        const minuteStr = minutes.toString().padStart(2, '0');
        let newMinuteStr = '';
        let newCursorPos = cursorPos + 1;

        if (cursorPos === 3) {
          newMinuteStr = digit + minuteStr[1];
          // Auto-advance if digit > 5 (can't be valid minute)
          if (digit > 5) {
            newCursorPos = 6;
          }
        } else if (cursorPos === 4) {
          newMinuteStr = minuteStr[0] + digit;
          // Auto-advance after completing minutes
          newCursorPos = 6;
        } else if (cursorPos === 5) {
          // At colon, move to seconds
          newMinuteStr = minuteStr;
          newCursorPos = 6;
        }

        const newMinutes = Math.min(59, parseInt(newMinuteStr) || 0);
        const newTime = formatTimePreservingUnderscores(
          hours,
          newMinutes,
          seconds,
          time,
          'minutes'
        );
        triggerChange(newTime);
        setTimeout(
          () => input.setSelectionRange(newCursorPos, newCursorPos),
          0
        );
      } else if (currentSegment === 'seconds') {
        const secondStr = seconds.toString().padStart(2, '0');
        let newSecondStr = '';
        let newCursorPos = cursorPos + 1;

        if (cursorPos === 6) {
          newSecondStr = digit + secondStr[1];
          // Auto-advance if digit > 5 (can't be valid second)
          if (digit > 5) {
            newCursorPos = 8;
          }
        } else if (cursorPos === 7) {
          newSecondStr = secondStr[0] + digit;
          // Stay at end after completing seconds
          newCursorPos = 8;
        } else if (cursorPos === 8) {
          // At end, replace last digit
          newSecondStr = secondStr[0] + digit;
          newCursorPos = 8;
        }

        const newSeconds = Math.min(59, parseInt(newSecondStr) || 0);
        const newTime = formatTimePreservingUnderscores(
          hours,
          minutes,
          newSeconds,
          time,
          'seconds'
        );
        triggerChange(newTime);
        setTimeout(
          () => input.setSelectionRange(newCursorPos, newCursorPos),
          0
        );
      }
      return;
    }

    // Block all other keys
    e.preventDefault();
  };

  // Handle click - let browser position cursor naturally, just fix invalid positions
  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const input = e.currentTarget;

    setTimeout(() => {
      const cursorPos = input.selectionStart || 0;

      // Only adjust if cursor is exactly on a colon
      if (cursorPos === 2) {
        // On first colon, decide based on click position
        const rect = input.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const charWidth = rect.width / 8;
        const colonCenter = charWidth * 2.5;

        input.setSelectionRange(
          x < colonCenter ? 2 : 3,
          x < colonCenter ? 2 : 3
        );
      } else if (cursorPos === 5) {
        // On second colon, decide based on click position
        const rect = input.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const charWidth = rect.width / 8;
        const colonCenter = charWidth * 5.5;

        input.setSelectionRange(
          x < colonCenter ? 5 : 6,
          x < colonCenter ? 5 : 6
        );
      }
    }, 0);
  };

  // Update time from picker
  const updateTimeFromPicker = (
    newHours: number,
    newMinutes: number,
    newSeconds: number
  ) => {
    const formattedTime = formatTime(newHours, newMinutes, newSeconds);
    triggerChange(formattedTime);
  };

  // Generate number arrays for picker
  const hourOptions = Array.from({ length: 24 }, (_, i) => i);
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i);
  const secondOptions = Array.from({ length: 60 }, (_, i) => i);

  const { hours, minutes, seconds } = parseTime(time);

  return (
    <div className={cn('relative', className)}>
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={time}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onClick={handleClick}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="pr-10 font-mono"
          readOnly={false}
        />
        <Popover open={isOpen} onOpenChange={setIsOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
            >
              <PiClock className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0 ml-2"
            align="start"
            side={'right'}
          >
            <div className="p-4">
              <div className="flex gap-2">
                {/* Hours */}
                <div className="flex flex-col items-center">
                  <div className="text-xs text-muted-foreground mb-1">
                    Hours
                  </div>
                  <ScrollArea className="h-32 w-16 border rounded">
                    {hourOptions.map((hour) => (
                      <button
                        key={hour}
                        onClick={() =>
                          updateTimeFromPicker(hour, minutes, seconds)
                        }
                        className={cn(
                          'w-full px-2 py-1 text-sm hover:bg-accent hover:text-accent-foreground',
                          hour === hours && 'bg-primary text-primary-foreground'
                        )}
                      >
                        {hour.toString().padStart(2, '0')}
                      </button>
                    ))}
                  </ScrollArea>
                </div>

                {/* Minutes */}
                <div className="flex flex-col items-center">
                  <div className="text-xs text-muted-foreground mb-1">
                    Minutes
                  </div>
                  <ScrollArea className="h-32 w-16 border rounded">
                    {minuteOptions.map((minute) => (
                      <button
                        key={minute}
                        onClick={() =>
                          updateTimeFromPicker(hours, minute, seconds)
                        }
                        className={cn(
                          'w-full px-2 py-1 text-sm hover:bg-accent',
                          minute === minutes &&
                            'bg-primary text-primary-foreground'
                        )}
                      >
                        {minute.toString().padStart(2, '0')}
                      </button>
                    ))}
                  </ScrollArea>
                </div>

                {/* Seconds */}
                <div className="flex flex-col items-center">
                  <div className="text-xs text-muted-foreground mb-1">
                    Seconds
                  </div>
                  <ScrollArea className="h-32 w-16 border rounded">
                    {secondOptions.map((second) => (
                      <button
                        key={second}
                        onClick={() =>
                          updateTimeFromPicker(hours, minutes, second)
                        }
                        className={cn(
                          'w-full px-2 py-1 text-sm hover:bg-accent',
                          second === seconds &&
                            'bg-primary text-primary-foreground'
                        )}
                      >
                        {second.toString().padStart(2, '0')}
                      </button>
                    ))}
                  </ScrollArea>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
